import { Bookmark, User as UserIcon, MessageCircle, Quote } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { getShimmerDataURL } from '../../utils/imageUtils';
import { CurationDropdown } from '../products/CurationDropdown';
import { VisitSiteButton } from '../shared/VisitSiteButton';
import { CommentsPanel } from '../products/CommentsPanel';
import { useIsMobile } from '../ui/use-mobile';
import type { ItemCardItem, ItemImage, Curation } from '../../types/item';

interface ItemCardProps {
  item: ItemCardItem;
  onSaveToggle?: (saved: boolean) => void;
  onClick?: () => void;
  currentUserId?: string;
  currentUser?: { avatar_url?: string; first_name?: string };
  roundedCorners?: boolean;
  // Curation props (batch-loaded from parent to avoid N+1 queries)
  userCurations?: Curation[];
  curationsWithItem?: Set<string>;
  onToggleCuration?: (curationId: string) => Promise<void>;
  onCreateCuration?: (title: string) => Promise<Curation | void>;
  // Comments coordination (managed by parent to ensure only one card has comments open)
  isCommentsOpen?: boolean;
  onCommentsOpen?: (contributionId: string) => void;
  onCommentsClose?: () => void;
}

export function ItemCard({
  item,
  onSaveToggle,
  onClick,
  currentUserId,
  currentUser,
  roundedCorners = false,
  // Curation props with defaults for backwards compatibility
  userCurations = [],
  curationsWithItem = new Set(),
  onToggleCuration,
  onCreateCuration,
  // Comments coordination
  isCommentsOpen = false,
  onCommentsOpen,
  onCommentsClose
}: ItemCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [isSaved, setIsSaved] = useState(item.isSaved || item.is_saved || false);
  const ownerCommentOffset = item.comment ? 1 : 0;
  const [commentsCount, setCommentsCount] = useState((item.comments_count || 0) + ownerCommentOffset);

  // Track screen size to determine comment panel mode
  useEffect(() => {
    const handler = () => setIsLargeScreen(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Sync comment count when parent updates (e.g., from flyover panel)
  useEffect(() => {
    setCommentsCount((item.comments_count || 0) + ownerCommentOffset);
  }, [item.comments_count, ownerCommentOffset]);

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Local curation dropdown state
  const [showCurationDropdown, setShowCurationDropdown] = useState(false);
  const [showCreateCuration, setShowCreateCuration] = useState(false);
  const [newCurationTitle, setNewCurationTitle] = useState('');
  const [isCreatingCuration, setIsCreatingCuration] = useState(false);
  const curationDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (curationDropdownRef.current && !curationDropdownRef.current.contains(event.target as Node)) {
        setShowCurationDropdown(false);
      }
    };

    if (showCurationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCurationDropdown]);

  // Handle toggling curation membership
  const handleToggleCuration = async (curationId: string) => {
    if (onToggleCuration) {
      try {
        await onToggleCuration(curationId);
      } catch (err: any) {
        console.error('Error toggling curation:', err);
        alert(err.message || 'Failed to update curation');
      }
    }
  };

  // Handle creating new curation
  const handleCreateCuration = async () => {
    if (!onCreateCuration || !newCurationTitle.trim()) return;

    try {
      setIsCreatingCuration(true);
      await onCreateCuration(newCurationTitle.trim());
      setNewCurationTitle('');
      setShowCreateCuration(false);
    } catch (err: any) {
      console.error('Error creating curation:', err);
      alert(err.message || 'Failed to create curation');
    } finally {
      setIsCreatingCuration(false);
    }
  };

  // Sync local state with prop changes
  useEffect(() => {
    setIsSaved(item.isSaved || item.is_saved || false);
  }, [item.isSaved, item.is_saved]);

  const handleSaveToggle = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newSavedState = !isSaved;
    setIsSaved(newSavedState);
    onSaveToggle?.(newSavedState);
  };

  const handleCardClick = () => {
    // On large screens with flyover, clicking card navigates to item detail
    if (isCommentsOpen && isLargeScreen) {
      if (item.id) {
        navigate(`/product/${item.id}`, {
          state: location.state
        });
      }
      return;
    }

    // On smaller screens, close comments panel if open
    if (isCommentsOpen && !isLargeScreen) {
      onCommentsClose?.();
      return;
    }

    if (onClick) {
      onClick();
    } else if (item.id) {
      // Preserve the navigation context (curated, brands, etc.)
      navigate(`/product/${item.id}`, {
        state: location.state
      });
    }
  };

  const handleCommentsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCommentsOpen && onCommentsClose) {
      onCommentsClose();
    } else if (item.id && onCommentsOpen) {
      onCommentsOpen(item.id);
    }
  };

  const handleCommentsClose = () => {
    onCommentsClose?.();
  };

  // Compute cached image URL
  const highlightedImage = item.images?.find((img: ItemImage) => img.highlighted);
  const cachedImageUrl = highlightedImage?.cached_url ||
                         item.cached_image_url ||
                         item.cached_image ||
                         item.images?.[0]?.cached_url;

  const isHighlighted = isCommentsOpen && isLargeScreen

  return (
    <div
      className={`relative bg-white overflow-hidden cursor-pointer transition-all duration-200 ${
        roundedCorners ? 'rounded-lg' : ''
      } ${
        isHighlighted
          ? 'ring-2 ring-[#57241F] ring-offset-2 shadow-xl z-[35]'
          : 'hover:opacity-95'
      }`}
      onClick={handleCardClick}
      style={{
        boxShadow: isHighlighted ? undefined : '0 2px 8px rgba(0, 0, 0, 0.12)',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
      }}
    >
      {/* Image Container */}
      <div className="relative group">
        {!cachedImageUrl ? (
          // No cached image available - show placeholder
          <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
            <span className="text-gray-500 text-sm">Image pending</span>
          </div>
        ) : (
          <>
            {/* Blur-up placeholder - shows immediately while full image loads */}
            {!imageLoaded && !imageError && (
              <div className="relative w-full" style={{ paddingBottom: '100%' }}>
                <img
                  src={getShimmerDataURL()}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover blur-sm scale-110"
                  style={{
                    filter: 'blur(20px)',
                    transform: 'scale(1.1)',
                  }}
                />
              </div>
            )}

            {!imageError ? (
              <img
                src={cachedImageUrl}
                alt={item.title}
                className={`w-full h-auto object-cover transition-all duration-500 ease-out ${
                  imageLoaded ? 'opacity-100 blur-0' : 'opacity-0 blur-sm absolute inset-0'
                }`}
                loading="lazy"
                style={{
                  display: 'block',
                  willChange: imageLoaded ? 'auto' : 'opacity, filter',
                }}
                onLoad={() => setImageLoaded(true)}
                onError={() => {
                  setImageError(true);
                  setImageLoaded(true);
                }}
              />
            ) : (
              <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                <span className="text-gray-500">Failed to load image</span>
              </div>
            )}
          </>
        )}

        {/* Quote Overlay - shown on hover when item has a comment/quote (desktop only, not when comments/flyover open) */}
        {!isMobile && item.comment && !isCommentsOpen && !isHighlighted && (
          <div
            className="absolute inset-0 bg-[#3D1815]/80 opacity-0 group-hover:opacity-100 flex flex-col justify-center items-center p-6 text-center transition-opacity duration-300"
            style={{
              transform: 'translate3d(0, 0, 0)',
              WebkitTransform: 'translate3d(0, 0, 0)',
            }}
          >
            {/* Quote Icon */}
            <Quote className="text-white/40 w-8 h-8 mb-3 rotate-180" />

            {/* The Quote */}
            <p className="text-white text-sm md:text-base leading-relaxed line-clamp-4" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: 'italic' }}>
              "{item.comment}"
            </p>

            {/* User Attribution */}
            {item.user && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.user?.username) {
                    navigate(`/profile/${item.user.username}`);
                  }
                }}
                className="mt-4 flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                {item.user?.avatar_url ? (
                  <img
                    src={item.user.avatar_url}
                    alt={item.user?.first_name || 'User'}
                    className="h-7 w-7 rounded-full object-cover border border-white/30"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center border border-white/30">
                    <UserIcon className="h-3.5 w-3.5 text-white/70" />
                  </div>
                )}
                <span className="text-white text-xs font-medium tracking-wide uppercase">
                  {item.user?.first_name || ''} {item.user?.last_name || ''}
                </span>
              </button>
            )}

            {/* Visit Site button - bottom right on quote overlay */}
            {item.source_url && (
              <div className="absolute bottom-4 right-4">
                <VisitSiteButton url={item.source_url} variant="dark" />
              </div>
            )}
          </div>
        )}

        {/* Standard hover elements - only shown when NO quote (desktop only, not when comments/flyover open) */}
        {!isMobile && !item.comment && !isCommentsOpen && !isHighlighted && (
          <>
            {/* User Avatar on hover - top left */}
            {item.user && (
              <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.user?.username) {
                      navigate(`/profile/${item.user.username}`);
                    }
                  }}
                  className="flex items-center gap-2 p-1.5 pr-3 rounded-full bg-white/90 backdrop-blur hover:bg-white shadow-lg"
                  title={`View ${item.user?.first_name || ''} ${item.user?.last_name || ''}'s profile`}
                >
                  {item.user?.avatar_url ? (
                    <img
                      src={item.user.avatar_url}
                      alt={item.user?.first_name || 'User'}
                      className="h-6 w-6 rounded-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center">
                      <UserIcon className="h-3.5 w-3.5 text-gray-500" />
                    </div>
                  )}
                  <span className="text-[10px] font-bold tracking-widest uppercase text-black">
                    {item.user?.first_name || ''} {item.user?.last_name || ''}
                  </span>
                </button>
              </div>
            )}

            {/* Visit Site button on hover - bottom right */}
            {item.source_url && (
              <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <VisitSiteButton url={item.source_url} variant="light" />
              </div>
            )}
          </>
        )}

        {/* Bookmark overlay - always visible on card image */}
        <div className="absolute top-3 right-3 z-10">
            {currentUserId ? (
              <CurationDropdown
                dropdownRef={curationDropdownRef}
                showDropdown={showCurationDropdown}
                setShowDropdown={setShowCurationDropdown}
                userCurations={userCurations}
                curationsWithItem={curationsWithItem}
                showCreateCuration={showCreateCuration}
                setShowCreateCuration={setShowCreateCuration}
                newCurationTitle={newCurationTitle}
                setNewCurationTitle={setNewCurationTitle}
                isCreatingCuration={isCreatingCuration}
                onToggleCuration={handleToggleCuration}
                onCreateCuration={handleCreateCuration}
                variant="icon"
                isSaved={isSaved}
                onSaveToggle={handleSaveToggle}
                dropdownPosition="below"
                customTrigger={(toggleDropdown) => (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isSaved) {
                        handleSaveToggle();
                      }
                      toggleDropdown();
                    }}
                    className="rounded-full p-1 transition-all hover:scale-110"
                  >
                    <Bookmark
                      className={`h-[18px] w-[18px] md:h-6 md:w-6 transition-colors drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)] ${
                        isSaved
                          ? 'text-gray-500 fill-current'
                          : 'text-white'
                      }`}
                    />
                  </button>
                )}
              />
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveToggle();
                }}
                className="rounded-full p-1 transition-all hover:scale-110"
              >
                <Bookmark
                  className={`h-4 w-4 transition-colors ${
                    isSaved
                      ? 'text-black fill-current'
                      : 'text-gray-600'
                  }`}
                />
              </button>
            )}
          </div>

      </div>

      {/* Comments Panel — shown on screens < 1024px (mobile + tablet) */}
      {!isLargeScreen && item.id && currentUserId && createPortal(
        <AnimatePresence>
          {isCommentsOpen && (
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-x-0 top-[73px] bottom-0 z-50 bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <CommentsPanel
                contributionId={item.id}
                currentUserId={currentUserId}
                currentUser={currentUser ? {
                  first_name: currentUser.first_name || '',
                  last_name: '',
                  username: '',
                  avatar_url: currentUser.avatar_url || ''
                } : undefined}
                ownerComment={item.comment}
                ownerUser={item.user ? {
                  first_name: item.user.first_name || '',
                  last_name: item.user.last_name || '',
                  username: item.user.username || '',
                  avatar_url: item.user.avatar_url || ''
                } : undefined}
                onClose={handleCommentsClose}
                isMobile={true}
                onCommentAdded={() => setCommentsCount(prev => prev + 1)}
                onCommentDeleted={() => setCommentsCount(prev => Math.max(0, prev - 1))}
                itemTitle={item.title}
                itemImageUrl={cachedImageUrl}
              />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Content */}
      <div className="p-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm text-gray-900 line-clamp-2" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 400 }}>
              {item.title}
            </h3>
          </div>
          <button
            onClick={handleCommentsClick}
            className="flex items-center gap-1 group/comment hover:scale-105 transition-transform flex-shrink-0 mt-0.5"
          >
            <MessageCircle className={`h-4 w-4 transition-colors ${
              isCommentsOpen
                ? 'text-[#57241F]'
                : 'text-gray-400 group-hover/comment:text-gray-600'
            }`} />
            <span className="text-xs text-gray-600" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
              {commentsCount}
            </span>
          </button>
        </div>
        {item.brand && (
          <p className="text-xs text-gray-600 mt-1" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
            {item.brand}
          </p>
        )}
      </div>
    </div>
  );
}
